/**
 * What the search page's car bar shows, read from a car payload (P25009-491).
 *
 * Pure functions over the same payload every car flow produces — a plate
 * lookup, a car chosen by model, the landing picker — so the bar, its tests
 * and the old sidebar agree on what a car's sizes and measurements are. The
 * readers are the ones the sidebar car panel (CarInfoSidebar) used since
 * P25009-364 / P25009-486; they moved here when the bar replaced it.
 *
 * Three envelopes reach this code:
 *   licence-plate API   { result: {...} }  or  { data: [{...}] }
 *   car database        { data: [{...}] }
 *   landing picker      { car: {...} }
 */
import {
  combinationParamForSetup,
  getCarSetups,
  getPrimarySetup,
  setupPair,
} from '../../../util/carSetups';
import { carFitment, carSearchParams, parseRimSize } from '../../../util/carSearchParams';
import { wheelDiametersOfCar } from '../../../util/wheelFitment';

/* A tire dimension must look like "225/50R17" or "225/50ZR17". The fitment
 * DB occasionally returns malformed strings (e.g. "13175") that would
 * otherwise leak into the size list — gate every push on this regex. */
const TIRE_RE = /^\d+\/\d+\s*Z?R\d+/i;

/* A rim spec looks like "7.5Jx18", "8JX18", or "7Jx18 ET45". Filters out junk
 * like bare numbers ("13174") that occasionally appear in rim fields. */
const RIM_RE = /^[\d.]+\s*J?\s*[xX]\s*\d+/;

/* Pick the per-record object regardless of response envelope. */
export const pickCar = data => {
  if (!data) return {};
  return data.car || (Array.isArray(data.data) ? data.data[0] : null) || data.result || data || {};
};

/** "Volvo XC60" (+ variant) from any of the payload shapes. */
export const resolveCarTitle = data => {
  const car = pickCar(data);
  const make = car?.make?.name || car?.brand || data?.result?.make || '';
  const model =
    car?.model?.name ||
    car?.series?.name ||
    car?.series ||
    car?.model ||
    car?.sales_name ||
    data?.result?.model ||
    '';
  const suffix = car?.name || '';
  return [make, model, suffix].filter(Boolean).join(' ').trim();
};

/** Bolt pattern, hub bore, offset and stud thread, as the payload has them. */
export const resolveSpecs = data => {
  const tireInfo =
    data?.car || (Array.isArray(data?.data) ? data.data[0] : {}) || data?.result?.tire_info || {};
  const tech = tireInfo?.technical || {};

  let boltPattern = tech.bolt_pattern || '';
  if (!boltPattern && tech.stud_holes && tech.pcd) {
    boltPattern = `${tech.stud_holes}-${tech.pcd}`;
  }
  if (!boltPattern) {
    const bolts = tireInfo?.bolts || '';
    const circle = tireInfo?.bolt_circle || '';
    if (bolts && circle) boltPattern = `${bolts}-${circle}`;
  }

  const centerBore =
    tech.centre_bore || tireInfo?.center_bore || data?.result?.tire_info?.center_bore || '';

  let et = '';
  const frontWheel = tireInfo?.wheels?.[0]?.front;
  if (frontWheel?.rim_offset != null) {
    et = frontWheel.rim_offset.toString();
  } else {
    const frontRims = data?.result?.original_tires?.front_rims || frontWheel?.rim || '';
    if (typeof frontRims === 'string' && frontRims.includes('ET')) {
      et = frontRims.split('ET')[1];
    } else if (typeof frontRims === 'string' && frontRims.includes('x')) {
      et = frontRims.split('x')[2] || '';
    }
  }
  et = et?.replace?.(/\s+/g, '') || '';

  return {
    boltPattern: boltPattern || null,
    hubHole: centerBore ? `${centerBore} mm` : null,
    offset: et ? `${et} mm` : null,
    nutThread: tech.stud_thread || null,
  };
};

/* The car's tyre sizes (max 8), malformed strings dropped. */
const resolveRecommendedDimensions = data => {
  const out = new Set();
  const push = val => {
    if (typeof val !== 'string') return;
    const trimmed = val.trim();
    if (trimmed && TIRE_RE.test(trimmed)) out.add(trimmed);
  };
  const original = data?.result?.original_tires || data?.car?.original_tires || {};
  push(original.front_tires);
  push(original.rear_tires);
  const tires = data?.car?.tire_info?.tires || data?.result?.tire_info?.tires || [];
  tires.forEach(t => {
    push(t?.tire);
    push(t?.front?.tire);
    push(t?.rear?.tire);
  });
  const dataList = Array.isArray(data?.data) ? data.data : [];
  dataList.forEach(d => {
    (d?.wheels || []).forEach(w => {
      push(w?.front?.tire);
      push(w?.rear?.tire);
    });
  });
  return Array.from(out).slice(0, 8);
};

/* The car's rim sizes (max 8), malformed strings dropped. */
const resolveRecommendedRims = data => {
  const out = new Set();
  const push = val => {
    if (typeof val !== 'string') return;
    const trimmed = val.trim();
    if (trimmed && RIM_RE.test(trimmed)) out.add(trimmed);
  };
  const original = data?.result?.original_tires || data?.car?.original_tires || {};
  push(original.front_rims);
  push(original.rear_rims);
  const tires = data?.car?.tire_info?.tires || data?.result?.tire_info?.tires || [];
  tires.forEach(t => {
    push(t?.front?.rim);
    push(t?.rear?.rim);
  });
  const dataList = Array.isArray(data?.data) ? data.data : [];
  dataList.forEach(d => {
    (d?.wheels || []).forEach(w => {
      push(w?.front?.rim);
      push(w?.rear?.rim);
    });
  });
  return Array.from(out).slice(0, 8);
};

/* The car's original (OE) sizes — the payload's original_tires. The fitment
 * database has no per-size OE flag; original_tires is its first listed size,
 * the closest thing it has (P25009-486 BR-1). */
const sizeKey = value =>
  String(value || '')
    .replace(/\.00/g, '')
    .replace(/\s+/g, '')
    .toUpperCase();
const resolveOriginalSizes = data => {
  const original = data?.result?.original_tires || data?.car?.original_tires || {};
  const car = Array.isArray(data?.data) ? data.data[0] : null;
  const fromFitment = car?.original_tires || {};
  return new Set(
    [
      original.front_tires,
      original.rear_tires,
      original.front_rims,
      original.rear_rims,
      fromFitment.front_tires,
      fromFitment.rear_tires,
      fromFitment.front_rims,
      fromFitment.rear_rims,
    ]
      .filter(value => typeof value === 'string' && value.trim())
      .map(sizeKey)
  );
};

/* Swedish decimals: "63.4" → "63,4". */
const sv = value => String(value).replace(/(\d)\.(\d)/g, '$1,$2');

/** "8JX20" → "8×20″", "7.5Jx18 ET50.5" → "7,5×18″ ET50,5". */
export const rimText = rim => {
  const parsed = parseRimSize(rim);
  if (!parsed) return String(rim || '').replace(/\.00/g, '');
  const size = parsed.width ? `${sv(parsed.width)}×${parsed.diameter}″` : `${parsed.diameter}″`;
  return parsed.offset != null ? `${size} ET${sv(parsed.offset)}` : size;
};

/* What a chip says: a tyre as written, a rim the readable way. */
const sizeText = (size, adType) =>
  adType === 'rim' ? rimText(size) : String(size || '').replace(/\.00/g, '');

/* The dimension filters a size uses when it is not a pair — the ones the size
 * chips have always used. `combination` is an index many listings still lack,
 * so only a real front+rear pair goes through it (nothing else can express
 * "covers both axles"). */
const dimensionParamsFor = (setup, adType) => {
  const [front] = setupPair(setup, adType);
  if (adType === 'rim') {
    const m = /[xX](\d+)/.exec(setup.frontRimRaw || '') || /^(\d+)\s*ET/i.exec(front || '');
    return m ? { pub_diameter: m[1] } : null;
  }
  const m = /(\d+)\/(\d+)\s*Z?R(\d+)/.exec(front || '');
  return m ? { pub_tireWidthFront: m[1], pub_profileFront: m[2], pub_diameter: m[3] } : null;
};

/* The size params the URL shows — what a chip is "active" against. */
const SIZE_KEYS = ['pub_tireWidthFront', 'pub_profileFront', 'pub_diameter', 'pub_rimWidthFront'];

/**
 * The search a set chip makes: the whole pair (`has_all`), one axle of it
 * (`axle`), or a square row through the plain dimension filters.
 */
const setSearchParams = ({ data, setup, adType, axle = null, hubHoleOptions }) => {
  const value = combinationParamForSetup(setup, adType, axle);
  if (!value) return null;
  const usesCombination = value.startsWith('has_all:') || !!axle;
  const dims = usesCombination ? null : dimensionParamsFor(setup, adType);
  if (!usesCombination && !dims) return null;
  const [front, rear] = setupPair(setup, adType);
  const primary = axle === 'rear' ? rear : front;
  return {
    ...carSearchParams({ data, adType, size: usesCombination ? null : primary, hubHoleOptions }),
    ...(usesCombination ? { pub_combination: value } : dims),
    ...(axle ? { axle } : {}),
  };
};

/**
 * One chip per size the car takes, for the product in view (BR-3).
 *
 * A car with a staggered fitment gets one chip per complete SET — "235/55R19
 * fram · 255/50R19 bak", tagged Komplett sats — so a listing that covers one
 * axle never looks like an answer (P25009-364); its square rows stay chips of
 * their own. A square car gets one chip per size, tagged Original or Tillval.
 * Split-diameter setups (19″ front, 21″ rear) cannot be one listing's set and
 * are left out of the set list (P25009-364 BR-5).
 *
 * @param {Object} options
 * @param {Object} options.data     car payload
 * @param {string} options.adType   'wheel' | 'tire' | 'rim'
 * @param {string} options.search   location.search, for which chip is active
 * @param {Array}  [options.hubHoleOptions]
 * @returns {Array<Object>} chips: { key, front, rear, tag, isSet, active,
 *   params, frontParams?, rearParams?, activeAxle, setup }
 */
export const sizeChipsFor = ({ data, adType, search, hubHoleOptions }) => {
  if (!data) return [];
  const url = new URLSearchParams(search || '');
  const activeCombination = url.get('pub_combination') || '';
  const activeAxle = url.get('axle') || '';
  const originalSizes = resolveOriginalSizes(data);
  const setups = getCarSetups(data);

  const options = setups
    .filter(setup => !setup.isSplitDiameter)
    .map(setup => ({ setup, value: combinationParamForSetup(setup, adType) }))
    .filter(option => option.value)
    .filter((option, i, all) => all.findIndex(o => o.value === option.value) === i);
  const hasPair = options.some(option => option.value.startsWith('has_all:'));

  if (hasPair) {
    return options
      .map(({ setup, value }) => {
        const isSet = value.startsWith('has_all:');
        const [front, rear] = setupPair(setup, adType);
        const params = setSearchParams({ data, setup, adType, hubHoleOptions });
        if (!params) return null;
        let active;
        if (isSet) {
          const single = activeAxle ? combinationParamForSetup(setup, adType, activeAxle) : '';
          active = activeCombination === value || (!!single && activeCombination === single);
        } else {
          const dims = dimensionParamsFor(setup, adType);
          active =
            !!dims &&
            !activeCombination &&
            Object.entries(dims).every(([key, v]) => url.get(key) === v);
        }
        return {
          key: value,
          front: sizeText(front, adType),
          rear: isSet ? sizeText(rear, adType) : '',
          tag: isSet ? 'Komplett sats' : setup.isOem ? 'Original' : 'Tillval',
          isSet,
          active,
          activeAxle: isSet && active ? activeAxle || null : null,
          params,
          frontParams: isSet
            ? setSearchParams({ data, setup, adType, axle: 'front', hubHoleOptions })
            : null,
          rearParams: isSet
            ? setSearchParams({ data, setup, adType, axle: 'rear', hubHoleOptions })
            : null,
          setup,
        };
      })
      .filter(Boolean);
  }

  const sizes = adType === 'rim' ? resolveRecommendedRims(data) : resolveRecommendedDimensions(data);
  return sizes
    .map(size => {
      const params = carSearchParams({ data, adType, size, hubHoleOptions });
      if (!params.pub_diameter) return null;
      const active =
        !activeCombination &&
        SIZE_KEYS.every(key => (params[key] || null) === (url.get(key) || null));
      const setup =
        setups.find(s =>
          adType === 'rim'
            ? sizeKey(s.frontRimRaw) === sizeKey(size)
            : sizeKey(s.frontTire) === sizeKey(size)
        ) || null;
      return {
        key: size,
        front: sizeText(size, adType),
        rear: '',
        tag: originalSizes.has(sizeKey(size)) ? 'Original' : 'Tillval',
        isSet: false,
        active,
        activeAxle: null,
        params,
        frontParams: null,
        rearParams: null,
        setup,
      };
    })
    .filter(Boolean);
};

/**
 * A car whose fitment has 19″ front and 21″ rear cannot be shopped as one set
 * (a listing has one diameter, P25009-364 BR-5). When that is why the chips
 * are single sizes, the bar says so, so the missing sets don't read as a bug.
 */
export const showsSplitDiameterNote = ({ data, adType }) => {
  if (!data) return false;
  const setups = getCarSetups(data);
  if (!setups.some(setup => setup.isSplitDiameter)) return false;
  return !setups
    .filter(setup => !setup.isSplitDiameter)
    .some(setup => combinationParamForSetup(setup, adType).startsWith('has_all:'));
};

/**
 * The "Teknisk data" table (BR-4): one row per axle of the size in view —
 * one "Fram & bak" row for a same-size fitment, Fram and Bak for a staggered
 * one. The size in view is the active chip's; with none, the car's original.
 *
 * ET is the fitment row's per axle. The car database often has no rear ET of
 * its own; the row then carries the front's (the same fallback the fitment
 * mapping uses), and an OEM record with no offset at all falls back to the
 * car's offset.
 *
 * @returns {Array<{ pos, tyre, rim, et }>}
 */
export const axleRowsFor = ({ data, chip }) => {
  if (!data) return [];
  const setup = chip?.setup || getPrimarySetup(data);
  if (!setup) return [];
  const carOffset = carFitment(data).offset;
  const etText = value => (value != null ? sv(value) : carOffset != null ? sv(carOffset) : '–');
  const front = {
    tyre: setup.frontTire || '–',
    rim: setup.frontRimRaw ? rimText(setup.frontRimRaw) : '–',
    et: etText(setup.frontOffset),
  };
  if (!setup.isStaggered) return [{ pos: 'Fram & bak', ...front }];
  return [
    { pos: 'Fram', ...front },
    {
      pos: 'Bak',
      tyre: setup.rearTire || '–',
      rim: setup.rearRimRaw ? rimText(setup.rearRimRaw) : '–',
      et: etText(setup.rearOffset ?? setup.frontOffset),
    },
  ];
};

/**
 * The car line of "Teknisk data" (BR-4): bolt pattern, hub bore, stud and
 * the wheel sizes it takes. TPMS is not in the car database (P25009-489: the
 * sensor model decides, not the car), so it is not shown rather than guessed.
 *
 * @returns {Array<{ key, label, value }>}
 */
export const carFactsFor = data => {
  if (!data) return [];
  const { boltPattern, hubHole, nutThread } = resolveSpecs(data);
  const diameters = wheelDiametersOfCar(pickCar(data))
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  const wheelSizes =
    diameters.length === 0
      ? null
      : diameters.length === 1
        ? `${diameters[0]}″`
        : `${diameters[0]}–${diameters[diameters.length - 1]}″`;
  return [
    boltPattern
      ? { key: 'bolt', label: 'Bultcirkel', value: sv(boltPattern).replace(/[xX-]/, '×') }
      : null,
    hubHole ? { key: 'hub', label: 'Navhål', value: sv(hubHole) } : null,
    nutThread ? { key: 'stud', label: 'Bult', value: sv(nutThread).replace(/x/i, '×') } : null,
    wheelSizes ? { key: 'wheels', label: 'Hjulstorlek', value: wheelSizes } : null,
  ].filter(Boolean);
};
